import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCopy, FiDownload, FiExternalLink, FiLock, FiLayers, FiCpu, FiRefreshCw, FiUpload, FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { assetsApi, mintRequestsApi } from '../../api/smartTokenizerApi';
import { mapAssetToFrontend, formatEnumLabel } from '../../api/smartTokenizerMappers';
import { useClientConfig } from '../../contexts/ClientConfigContext';
import { useTokenization } from '../../contexts/TokenizationContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import { getOfflineSignerAddressForUser } from '../../api/offlineSignerPreferences';
import { usePersistedOfflineSignerAddress } from '../../hooks/usePersistedOfflineSignerAddress';
import { getApiErrorMessage } from '../../api/apiErrors';
import TransactionLookupDrawer from '../BlockchainExplorer/TransactionLookupDrawer';
import {
  buildSignerOutputTemplate,
  copyTextToClipboard,
  downloadJsonFile,
  formatPreparedPayload,
  parseSignedTransactionInput
} from '../../blockchain/offlineSigning';
import { formatUnits, parseUnits } from 'ethers';
import { RWA_SHARED_ERC20 } from '../../blockchain/rwaSharedErc20Artifact';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1150;
  padding: 1.5rem;
`;

const Surface = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  width: 100%;
  max-width: min(920px, 100%);
  max-height: min(90vh, 880px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  h2 {
    margin: 0;
    color: #fff;
    font-size: 1.2rem;
    font-weight: 700;
  }
  .sub {
    margin: 0.35rem 0 0;
    color: #94a3b8;
    font-size: 0.88rem;
    line-height: 1.4;
  }
`;

const Body = styled.div`
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  color: #cbd5e1;
  cursor: pointer;
  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.14);
  }
`;

const FormPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
  span.label {
    color: #e2e8f0;
  }
  input,
  textarea {
    padding: 0.55rem 0.65rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    font-size: 0.92rem;
    width: 100%;
    max-width: 100%;
  }
  textarea {
    min-height: 100px;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.8rem;
    resize: vertical;
  }
`;

const FlowSteps = styled.ol`
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  color: #a0a0a0;
  line-height: 1.55;
  font-size: 0.9rem;
  li {
    margin-bottom: 0.5rem;
  }
  strong {
    color: #e2e8f0;
    font-weight: 600;
  }
  code {
    font-size: 0.85em;
    color: #9bd6ff;
  }
`;

const StatusPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: #fff;
  .title {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #4facfe;
  }
  .message {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1rem;
  }
  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const PayloadBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  .title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  .hint {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }
  textarea {
    width: 100%;
    min-height: 160px;
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 1rem;
    resize: vertical;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.85rem;
  }
`;

const PayloadActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MiniButton = styled.button.attrs({ type: 'button' })`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const FilePickLabel = styled.label`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;
  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }
  input {
    display: none;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  align-items: center;
`;

const PrimaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  background: #4facfe;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #3d8bfe;
  }
  &:disabled {
    background: #2a2a3e;
    color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.14);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const SectionDivider = styled.div`
  margin: 1.25rem 0;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
`;

const ManualSectionTitle = styled.h3`
  margin: 0 0 0.5rem;
  color: #e2e8f0;
  font-size: 0.95rem;
  font-weight: 600;
`;

const ManualLead = styled.p`
  margin: 0 0 1rem;
  color: #94a3b8;
  font-size: 0.85rem;
  line-height: 1.45;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(79, 172, 254, 0.15);
  color: #9bd6ff;
  border: 1px solid rgba(79, 172, 254, 0.35);
`;

const HashLink = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  padding: 0;
  color: #7ec8ff;
  font-family: ui-monospace, monospace;
  font-size: 0.8rem;
  cursor: pointer;
  text-decoration: underline;
  text-align: left;
`;

const CustodyVaultPanel = styled.div`
  background: rgba(79, 172, 254, 0.08);
  border: 1px solid rgba(79, 172, 254, 0.28);
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
  margin-bottom: 1rem;
  font-size: 0.82rem;
  line-height: 1.5;
  color: #cbd5e1;
  .vk {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #94a3b8;
    margin-bottom: 0.35rem;
  }
  .addr {
    font-family: ui-monospace, monospace;
    font-size: 0.8rem;
    word-break: break-all;
    color: #e0f2fe;
  }
`;

const Warn = styled.p`
  margin: 0 0 1rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fcd34d;
  font-size: 0.85rem;
  line-height: 1.45;
`;

const RoadmapCard = styled.div`
  border: 1px solid rgba(147, 197, 253, 0.28);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  background: rgba(15, 23, 42, 0.55);
`;

const RoadmapTitle = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin-bottom: 0.65rem;
  font-weight: 600;
`;

const RoadmapStep = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.65rem 1rem;
  align-items: start;
  padding: 0.55rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  &:first-of-type {
    padding-top: 0;
  }
`;

const StepNum = styled.span`
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 8px;
  background: ${(p) => (p.$done ? 'rgba(34, 197, 94, 0.22)' : 'rgba(79, 172, 254, 0.18)')};
  color: ${(p) => (p.$done ? '#86efac' : '#7dd3fc')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.82rem;
  flex-shrink: 0;
`;

const RoadmapBody = styled.div`
  font-size: 0.84rem;
  color: #cbd5e1;
  line-height: 1.5;
  strong {
    color: #f1f5f9;
    font-weight: 600;
  }
  .txkind {
    display: block;
    margin-top: 0.2rem;
    font-size: 0.78rem;
    color: #94a3b8;
  }
`;

const SharePreviewPanel = styled.div`
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.28);
  border-radius: 10px;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.86rem;
  line-height: 1.55;
  color: #d1fae5;
  .big {
    font-size: 1.15rem;
    font-weight: 700;
    color: #a7f3d0;
    font-variant-numeric: tabular-nums;
  }
  .sub {
    margin-top: 0.35rem;
    font-size: 0.78rem;
    color: #6ee7b7;
    opacity: 0.95;
  }
`;

function shortenAddr(addr) {
  const a = String(addr || '').trim();
  if (a.length < 18) return a || '—';
  return `${a.slice(0, 10)}…${a.slice(-8)}`;
}

function formatWeiAsShares(weiStr, decimals) {
  try {
    const w = String(weiStr || '').trim();
    if (!w) return null;
    return formatUnits(w, decimals);
  } catch {
    return null;
  }
}

/** Unwrap ABI accidentally double-encoded as a JSON string (breaks Nethereum: "Function not found: mint"). */
function normalizeEvmContractAbiJson(abiText) {
  const t = String(abiText || '').trim();
  if (!t) return t;
  try {
    const first = JSON.parse(t);
    if (typeof first === 'string') {
      const inner = first.trim();
      if (inner.startsWith('[')) {
        const arr = JSON.parse(inner);
        if (Array.isArray(arr)) return JSON.stringify(arr);
      }
    }
  } catch {
    /* keep original */
  }
  return t;
}

function VaultFractionalizationModal({ asset, onClose, onUpdated }) {
  const { addressExplorerUrl, explorerConfigured } = useClientConfig();
  const { currentIssuer, session, isAdmin, isAuthorizedTokenizer } = useTokenIssuer();
  const tokenizerSimpleFractionUi = Boolean(isAuthorizedTokenizer && !isAdmin);
  const offlineSignerUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id || null;
  const [signerAddress, setSignerAddress] = usePersistedOfflineSignerAddress(offlineSignerUserId);

  const {
    prepareVaultLock,
    submitVaultLock,
    getVaultLockByAssetId,
    prepareFractionalShares,
    submitFractionalShares,
    getFractionalSharesByAssetId,
    getAssetById
  } = useTokenization();

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [mintedToken, setMintedToken] = useState(null);
  const [vaultLock, setVaultLock] = useState(null);
  const [fractional, setFractional] = useState(null);

  const [nftContractAddress, setNftContractAddress] = useState('');
  const [nftTokenId, setNftTokenId] = useState('');
  const [currentOwnerWallet, setCurrentOwnerWallet] = useState('');
  const [custodyVaultAddress, setCustodyVaultAddress] = useState('');
  const [custodyLockFunctionName, setCustodyLockFunctionName] = useState('safeTransferFrom');
  /** Asset class → collection contract (may differ from mint record if data was corrected after mint). */
  const [assetClassCollectionAddress, setAssetClassCollectionAddress] = useState('');

  const [preparedVault, setPreparedVault] = useState(null);
  const [signedVaultInput, setSignedVaultInput] = useState('');
  const [vaultBusy, setVaultBusy] = useState(false);

  const [fractionAbi, setFractionAbi] = useState('');
  const [fractionContractAddress, setFractionContractAddress] = useState('');
  const [recipientWallet, setRecipientWallet] = useState('');
  /** Human-readable ERC20 share count (e.g. 1_000_000); combined with decimals → wei for the mint tx. */
  const [shareQuantityInput, setShareQuantityInput] = useState('1');
  const [shareTokenDecimals, setShareTokenDecimals] = useState(18);
  const [tokenName, setTokenName] = useState('');
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [fractionFunctionName, setFractionFunctionName] = useState('mint');

  const [preparedFraction, setPreparedFraction] = useState(null);
  const [signedFractionInput, setSignedFractionInput] = useState('');
  const [fractionBusy, setFractionBusy] = useState(false);

  const [txDrawerHash, setTxDrawerHash] = useState(null);

  const fractionAmountWei = useMemo(() => {
    const s = shareQuantityInput.trim();
    if (!s) return '';
    try {
      return parseUnits(s, shareTokenDecimals).toString();
    } catch {
      return '';
    }
  }, [shareQuantityInput, shareTokenDecimals]);

  const shareAmountValid = Boolean(fractionAmountWei);

  const nftAddressMismatch = useMemo(() => {
    const m = String(mintedToken?.contractAddress || '').trim().toLowerCase();
    const c = String(assetClassCollectionAddress || '').trim().toLowerCase();
    return Boolean(m && c && m !== c);
  }, [mintedToken?.contractAddress, assetClassCollectionAddress]);

  const vaultLockPayloadExtras = useMemo(() => {
    const v = custodyVaultAddress.trim();
    const f = custodyLockFunctionName.trim();
    return {
      ...(v ? { vaultContractAddress: v } : {}),
      ...(f ? { functionName: f } : {})
    };
  }, [custodyVaultAddress, custodyLockFunctionName]);

  const vaultSignerTemplate = JSON.stringify(
    buildSignerOutputTemplate({
      summary: `Transaction 1 — Vault lock: NFT #${nftTokenId || '?'} ${custodyLockFunctionName || 'safeTransferFrom'} into custody vault (asset ${asset.id})`
    }),
    null,
    2
  );

  const fractionSignerTemplate = JSON.stringify(
    buildSignerOutputTemplate({
      summary: `Transaction 2 — Mint ERC20 fractional shares for locked asset ${asset.id}`
    }),
    null,
    2
  );

  const refreshSideData = useCallback(async () => {
    const vl = await getVaultLockByAssetId(asset.id);
    setVaultLock(vl);
    const fr = await getFractionalSharesByAssetId(asset.id);
    setFractional(fr);
    await getAssetById(asset.id, true);
    onUpdated?.();
  }, [asset.id, getAssetById, getFractionalSharesByAssetId, getVaultLockByAssetId, onUpdated]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingInitial(true);
      try {
        let minted = null;
        try {
          const { data } = await mintRequestsApi.getByAssetId(asset.id);
          minted = data;
        } catch (e) {
          if (e.response?.status !== 404) {
            throw e;
          }
        }
        if (cancelled) {
          return;
        }
        setMintedToken(minted);
        if (minted) {
          setNftContractAddress(minted.contractAddress || '');
          setNftTokenId(minted.tokenId || '');
          setCurrentOwnerWallet(minted.currentOwnerWallet || '');
        }
        try {
          const { data: rawAsset } = await assetsApi.getById(asset.id);
          const mapped = mapAssetToFrontend(rawAsset);
          if (!cancelled) {
            setCustodyVaultAddress(String(mapped.custodyVaultContractAddress || '').trim());
            setCustodyLockFunctionName(
              String(mapped.custodyVaultLockFunctionName || 'safeTransferFrom').trim() || 'safeTransferFrom'
            );
            setAssetClassCollectionAddress(String(mapped.contractAddress || '').trim());
            const ftAddr = String(mapped.fractionTokenContractAddress || '').trim();
            if (ftAddr) {
              setFractionContractAddress(ftAddr);
              setFractionFunctionName(
                String(mapped.fractionTokenMintFunctionName || 'mint').trim() || 'mint'
              );
              setTokenName(String(mapped.suggestedFractionTokenName || '').trim() || '');
              setTokenSymbol(String(mapped.suggestedFractionTokenSymbol || '').trim() || '');
              setFractionAbi((prev) => {
                if (String(prev || '').trim()) return prev;
                try {
                  // Use .abi (array), not .abiJson — abiJson is already a string; JSON.stringify(string) double-encodes
                  // and breaks Nethereum ABI parsing ("Function not found: mint").
                  return JSON.stringify(RWA_SHARED_ERC20.abi ?? [], null, 2);
                } catch {
                  return prev;
                }
              });
            }
          }
        } catch {
          if (!cancelled) {
            setCustodyVaultAddress('');
            setCustodyLockFunctionName('safeTransferFrom');
            setAssetClassCollectionAddress('');
          }
        }
        const vl = await getVaultLockByAssetId(asset.id);
        const fr = await getFractionalSharesByAssetId(asset.id);
        if (cancelled) {
          return;
        }
        setVaultLock(vl);
        setFractional(fr);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.message || e.message || 'Failed to load vault data');
        }
      } finally {
        if (!cancelled) {
          setLoadingInitial(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [asset.id, getFractionalSharesByAssetId, getVaultLockByAssetId]);

  useEffect(() => {
    if (!offlineSignerUserId) return;
    if (getOfflineSignerAddressForUser(offlineSignerUserId)) return;
    const w = String(currentIssuer?.walletAddress || '').trim();
    if (w) setSignerAddress(w);
  }, [offlineSignerUserId, currentIssuer?.walletAddress, setSignerAddress]);

  /**
   * Prefill recipient from offline signer when empty (typical case: minter wallet receives shares).
   * Does not overwrite after the user edits or clears then sets a value — clear the field to re-apply signer.
   */
  useEffect(() => {
    const s = String(signerAddress || '').trim();
    if (!s) return;
    setRecipientWallet((prev) => (String(prev || '').trim() ? prev : s));
  }, [signerAddress]);

  const vaultLocked = vaultLock?.status === 'Locked';
  const vaultPending = vaultLock && vaultLock.status === 'Submitted';
  const fractionDone = fractional?.status === 'Confirmed';
  const fractionPending = fractional && fractional.status === 'Submitted';
  const hasConfiguredCustodyVault = Boolean(custodyVaultAddress.trim());

  const resetVaultOfflineFlow = () => {
    setPreparedVault(null);
    setSignedVaultInput('');
  };

  const resetFractionOfflineFlow = () => {
    setPreparedFraction(null);
    setSignedFractionInput('');
  };

  const handleCopyVaultPayload = async () => {
    if (!preparedVault) return;
    await copyTextToClipboard(formatPreparedPayload(preparedVault));
    toast.success('Copied JSON');
  };

  const handleVaultSignedFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text) {
        toast.error('File was empty');
        return;
      }
      setSignedVaultInput(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  const handlePrepareVault = async () => {
    setVaultBusy(true);
    try {
      const data = await prepareVaultLock(asset.id, {
        signerAddress,
        nftContractAddress,
        nftContractAbi: '',
        nftTokenId,
        currentOwnerWallet,
        ...vaultLockPayloadExtras
      });
      setPreparedVault(data);
      toast.success('Unsigned vault lock transaction ready.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Prepare vault lock failed'));
    } finally {
      setVaultBusy(false);
    }
  };

  const handleSubmitVault = async () => {
    let signed;
    try {
      const parsed = parseSignedTransactionInput(signedVaultInput);
      signed = Array.isArray(parsed) ? parsed[0] : parsed;
    } catch (e) {
      toast.error(e.message || 'Paste signed raw transaction hex or signer JSON');
      return;
    }
    setVaultBusy(true);
    try {
      const preparedSummaryJson =
        preparedVault &&
        JSON.stringify({
          transactionLabel: 'Transaction 1 — vault lock',
          custodyVaultAddress: custodyVaultAddress.trim(),
          custodyLockFunctionName: custodyLockFunctionName.trim(),
          preparedUnsigned: preparedVault,
          nftContractAddress: nftContractAddress.trim(),
          nftTokenId: nftTokenId.trim(),
          currentOwnerWallet: currentOwnerWallet.trim()
        });

      const result = await submitVaultLock(asset.id, {
        signedTransactionHex: signed.signedTransactionHex,
        nftContractAddress,
        nftTokenId,
        currentOwnerWallet,
        signerAddress,
        preparedSummaryJson,
        ...vaultLockPayloadExtras
      });
      const vst = String(result?.status ?? '').toLowerCase();
      if (vst === 'failed') {
        toast.error(
          'Vault lock failed on-chain. Check the transaction in the explorer, or prepare again — the API simulates the lock with eth_call and surfaces the revert reason before you sign.'
        );
      } else if (vst === 'locked') {
        toast.success('NFT locked in custody vault.');
      } else {
        toast.success('Vault lock broadcast submitted.');
      }
      setVaultLock(result);
      resetVaultOfflineFlow();
      await refreshSideData();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Broadcast vault lock failed'));
    } finally {
      setVaultBusy(false);
    }
  };

  const handlePrepareFraction = async () => {
    if (!shareAmountValid) {
      toast.error('Enter a valid number of share tokens and decimals.');
      return;
    }
    if (!fractionContractAddress.trim()) {
      toast.error('Fraction token contract address is missing. Configure the share token on the asset class first.');
      return;
    }
    if (!normalizeEvmContractAbiJson(fractionAbi).trim()) {
      toast.error('Fraction token ABI is missing.');
      return;
    }
    if (!recipientWallet.trim()) {
      toast.error('Recipient wallet is missing. Set your offline signer address above.');
      return;
    }
    setFractionBusy(true);
    try {
      const data = await prepareFractionalShares(asset.id, {
        signerAddress,
        fractionTokenContractAddress: fractionContractAddress,
        fractionTokenContractAbi: normalizeEvmContractAbiJson(fractionAbi),
        recipientWallet,
        amount: fractionAmountWei,
        tokenName,
        tokenSymbol,
        functionName: fractionFunctionName || 'mint'
      });
      setPreparedFraction(data);
      toast.success('Unsigned fractional mint transaction ready.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Prepare fractional mint failed'));
    } finally {
      setFractionBusy(false);
    }
  };

  const handleSubmitFraction = async () => {
    let signed;
    try {
      const parsed = parseSignedTransactionInput(signedFractionInput);
      signed = Array.isArray(parsed) ? parsed[0] : parsed;
    } catch (e) {
      toast.error(e.message || 'Paste signed raw transaction hex or signer JSON');
      return;
    }
    setFractionBusy(true);
    try {
      const preparedSummaryJson =
        preparedFraction &&
        JSON.stringify({
          transactionLabel: 'Transaction 2 — fractional shares mint',
          preparedUnsigned: preparedFraction,
          fractionTokenContractAddress: fractionContractAddress.trim(),
          recipientWallet: recipientWallet.trim(),
          shareQuantityInput: shareQuantityInput.trim(),
          shareTokenDecimals,
          tokenName: tokenName.trim(),
          tokenSymbol: tokenSymbol.trim(),
          functionName: fractionFunctionName || 'mint',
          amountWei: fractionAmountWei
        });

      const result = await submitFractionalShares(asset.id, {
        signedTransactionHex: signed.signedTransactionHex,
        fractionTokenContractAddress: fractionContractAddress,
        recipientWallet,
        amount: fractionAmountWei,
        tokenName,
        tokenSymbol,
        functionName: fractionFunctionName || 'mint',
        signerAddress,
        shareTokenDecimals,
        shareQuantityHuman: shareQuantityInput.trim(),
        fractionTokenContractAbi: normalizeEvmContractAbiJson(fractionAbi),
        preparedSummaryJson
      });
      const fst = String(result?.status ?? '').toLowerCase();
      if (fst === 'failed') {
        toast.error(
          'Fractional mint failed on-chain. Check the transaction in the explorer or prepare again with corrected parameters.'
        );
      } else if (fst === 'confirmed') {
        toast.success('Fractional shares minted.');
      } else {
        toast.success('Fractional mint broadcast submitted.');
      }
      setFractional(result);
      resetFractionOfflineFlow();
      await refreshSideData();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Broadcast fractional mint failed'));
    } finally {
      setFractionBusy(false);
    }
  };

  const handleFractionSignedFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text) {
        toast.error('File was empty');
        return;
      }
      setSignedFractionInput(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  const handleCopyFractionPayload = async () => {
    if (!preparedFraction) return;
    await copyTextToClipboard(formatPreparedPayload(preparedFraction));
    toast.success('Copied JSON');
  };

  const busy = vaultBusy || fractionBusy;

  return (
    <>
      <TransactionLookupDrawer transactionHash={txDrawerHash} onClose={() => setTxDrawerHash(null)} />
      <AnimatePresence>
        <Overlay
          key={asset.id || 'vault-frac'}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
        >
          <Surface
            initial={{ scale: 0.94, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.94, opacity: 0, y: 12 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Head>
              <div>
                <h2>
                  <FiLock style={{ verticalAlign: '-3px', marginRight: '0.35rem', color: '#c4b5fd' }} />
                  Vault & fractional shares
                </h2>
                <p className="sub">
                  {asset.name} · {formatEnumLabel(asset.status)}
                  <span
                    style={{
                      display: 'block',
                      marginTop: '0.35rem',
                      fontSize: '0.88rem',
                      color: '#94a3b8',
                      lineHeight: 1.45
                    }}
                  >
                    <strong>Two separate on-chain transactions:</strong> first lock the NFT into the custody vault
                    (ERC-721 <code>safeTransferFrom</code>), then mint the ERC-20 share tokens that represent fractional
                    ownership of that locked NFT. Each step uses the same offline prepare → sign → broadcast flow as{' '}
                    <strong>Custody vault (fractionalization)</strong> on standard asset classes.
                  </span>
                </p>
              </div>
              <CloseBtn type="button" onClick={onClose} aria-label="Close" disabled={busy}>
                <FiX size={22} />
              </CloseBtn>
            </Head>

            <Body>
              {loadingInitial ? (
                <p style={{ color: '#94a3b8' }}>Loading mint and vault state…</p>
              ) : !mintedToken ? (
                <Warn>
                  No minted token is recorded for this asset. Complete NFT minting first, then open this flow again.
                </Warn>
              ) : (
                <>
                  <FormPanel>
                    <ManualSectionTitle style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <FiZap style={{ color: '#4facfe' }} aria-hidden />
                      Offline signer
                    </ManualSectionTitle>
                    <ManualLead>
                      One address for both vault lock and fractional mint. Remembered for your account in this browser.
                    </ManualLead>
                    <Field>
                      <span className="label">Offline signer address</span>
                      <input
                        value={signerAddress}
                        onChange={(e) => setSignerAddress(e.target.value)}
                        placeholder="0x…"
                        autoComplete="off"
                      />
                      <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                        Must match the wallet that signs in your offline signer (typically the NFT holder for the lock
                        step).
                      </span>
                    </Field>
                  </FormPanel>

                  <RoadmapCard>
                    <RoadmapTitle>Fractionalization roadmap</RoadmapTitle>
                    <RoadmapStep>
                      <StepNum $done={vaultLocked}>1</StepNum>
                      <RoadmapBody>
                        <strong>Transaction 1 — Lock NFT in vault</strong>
                        <span className="txkind">
                          On-chain: ERC-721 <code>safeTransferFrom</code> (your wallet → custody vault). Confirms the
                          underlying NFT is held in the class vault before shares are minted.
                        </span>
                      </RoadmapBody>
                    </RoadmapStep>
                    <RoadmapStep>
                      <StepNum $done={fractionDone}>2</StepNum>
                      <RoadmapBody>
                        <strong>Transaction 2 — Mint ERC-20 share tokens</strong>
                        <span className="txkind">
                          On-chain: ERC-20 <code>{fractionFunctionName || 'mint'}</code> on your fraction token contract.
                          Mints the number of share tokens you specify to the recipient wallet — those tokens represent
                          fractional claims on the locked NFT.
                        </span>
                      </RoadmapBody>
                    </RoadmapStep>
                  </RoadmapCard>

                  <SectionDivider />

                  <ManualSectionTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <StepNum $done={vaultLocked} style={{ width: '1.65rem', height: '1.65rem', fontSize: '0.75rem' }}>
                      1
                    </StepNum>
                    <FiLock style={{ color: '#93c5fd' }} aria-hidden />
                    Lock NFT in custody vault (transaction 1)
                  </ManualSectionTitle>
                  <ManualLead>
                    Unsigned transaction calls the NFT collection to move token #{nftTokenId || '…'} from the current owner
                    into the platform custody vault <code>{shortenAddr(custodyVaultAddress)}</code> (full address below).
                  </ManualLead>

                  {hasConfiguredCustodyVault ? (
                    <CustodyVaultPanel>
                      <div className="vk">Platform custody vault (asset class)</div>
                      <div className="addr">{custodyVaultAddress}</div>
                      <div style={{ marginTop: '0.45rem', color: '#94a3b8', fontSize: '0.78rem' }}>
                        Lock call: <strong style={{ color: '#bae6fd' }}>{custodyLockFunctionName}</strong>
                      </div>
                      <ActionRow style={{ marginTop: '0.55rem' }}>
                        <MiniButton
                          type="button"
                          onClick={() =>
                            copyTextToClipboard(custodyVaultAddress).then(() => toast.success('Vault address copied'))
                          }
                        >
                          <FiCopy /> Copy vault
                        </MiniButton>
                        <MiniButton
                          type="button"
                          disabled={!explorerConfigured}
                          onClick={() => {
                            const url = addressExplorerUrl(custodyVaultAddress);
                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <FiExternalLink /> Explorer
                        </MiniButton>
                      </ActionRow>
                    </CustodyVaultPanel>
                  ) : (
                    <Warn>
                      No custody vault is configured for this asset&apos;s class. An administrator must set it under{' '}
                      <strong>Asset classes</strong> → open the class → <strong>Custody vault</strong>, then reload this
                      dialog.
                    </Warn>
                  )}

                  {vaultLock && (
                    <div style={{ marginBottom: '1rem' }}>
                      <StatusPill>Vault: {formatEnumLabel(vaultLock.status)}</StatusPill>
                      {vaultLock.lockTransactionHash && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <HashLink type="button" onClick={() => setTxDrawerHash(vaultLock.lockTransactionHash)}>
                            {vaultLock.lockTransactionHash}
                          </HashLink>
                        </div>
                      )}
                    </div>
                  )}

                  {!vaultLocked && (
                    <>
                      <FormPanel>
                        {nftAddressMismatch && (
                          <Warn style={{ marginBottom: '0.85rem' }}>
                            Mint record collection <code>{mintedToken?.contractAddress}</code> differs from this asset
                            class collection <code>{assetClassCollectionAddress}</code>. The token lives on the contract
                            you actually minted to — wrong <code>to</code> reverts. Use the button that matches where
                            token #{nftTokenId || '…'} was minted.
                            <ActionRow style={{ marginTop: '0.55rem' }}>
                              <MiniButton
                                type="button"
                                onClick={() =>
                                  setNftContractAddress(String(mintedToken?.contractAddress || '').trim())
                                }
                              >
                                Use mint record
                              </MiniButton>
                              <MiniButton type="button" onClick={() => setNftContractAddress(assetClassCollectionAddress)}>
                                Use asset class collection
                              </MiniButton>
                            </ActionRow>
                          </Warn>
                        )}
                        <Field>
                          <span className="label">NFT contract address</span>
                          <input
                            value={nftContractAddress}
                            onChange={(e) => setNftContractAddress(e.target.value)}
                            placeholder="0x…"
                            autoComplete="off"
                          />
                        </Field>
                        <Row>
                          <Field>
                            <span className="label">Token ID</span>
                            <input
                              value={nftTokenId}
                              onChange={(e) => setNftTokenId(e.target.value)}
                              placeholder="1"
                              autoComplete="off"
                            />
                          </Field>
                          <Field>
                            <span className="label">Current owner wallet (from)</span>
                            <input
                              value={currentOwnerWallet}
                              onChange={(e) => setCurrentOwnerWallet(e.target.value)}
                              placeholder="0x…"
                              autoComplete="off"
                            />
                          </Field>
                        </Row>
                      </FormPanel>

                      <StatusPanel>
                        <div className="title">Transaction 1 — Offline signing &amp; broadcast (Ping chain)</div>
                        <FlowSteps>
                          <li>
                            <strong>Prepare unsigned vault lock</strong> — The API builds the ERC-721{' '}
                            <code>{custodyLockFunctionName || 'safeTransferFrom'}</code> calldata (NFT → vault) and runs
                            an <code>eth_call</code> simulation so mismatched contract / owner / token id surfaces before
                            you sign. Private keys never leave your offline signer.
                          </li>
                          <li>
                            <strong>Sign</strong> — Copy or download the JSON, then sign with the offline signer address
                            you entered (must be able to authorize the transfer as the NFT holder).
                          </li>
                          <li>
                            <strong>Upload or paste signed data</strong> — Then <strong>Broadcast signed vault lock</strong>{' '}
                            so the backend submits <code>eth_sendRawTransaction</code> for this lock transaction only.
                          </li>
                        </FlowSteps>
                        <div className="meta">
                          <span>Asset: {asset.name}</span>
                          <span>
                            NFT #{nftTokenId || '—'} · <code>{custodyLockFunctionName}</code>
                          </span>
                        </div>
                      </StatusPanel>

                      {preparedVault && (
                        <>
                          <PayloadBox>
                            <div className="title">Transaction 1 — Unsigned vault lock (NFT transfer)</div>
                            <div className="hint">Sign offline only — do not sign in the browser.</div>
                            <PayloadActions>
                              <MiniButton type="button" onClick={handleCopyVaultPayload}>
                                <FiCopy /> Copy JSON
                              </MiniButton>
                              <MiniButton
                                type="button"
                                onClick={() =>
                                  downloadJsonFile(`vault-lock-${asset.id}-prepared.json`, preparedVault)
                                }
                              >
                                <FiDownload /> Download JSON
                              </MiniButton>
                            </PayloadActions>
                            <textarea readOnly value={formatPreparedPayload(preparedVault)} />
                          </PayloadBox>

                          <PayloadBox>
                            <div className="title">Signer output template</div>
                            <div className="hint">Your signer should produce JSON containing signedTransactionHex.</div>
                            <PayloadActions>
                              <MiniButton
                                type="button"
                                onClick={async () => {
                                  await copyTextToClipboard(vaultSignerTemplate);
                                  toast.success('Copied template');
                                }}
                              >
                                <FiCopy /> Copy template
                              </MiniButton>
                            </PayloadActions>
                            <textarea readOnly value={vaultSignerTemplate} />
                          </PayloadBox>

                          <PayloadBox>
                            <div className="title">Transaction 1 — Signed vault lock — upload or paste</div>
                            <div className="hint">
                              Upload a JSON file from your signer or paste JSON / raw{' '}
                              <code>signedTransactionHex</code>.
                            </div>
                            <PayloadActions>
                              <FilePickLabel>
                                <FiUpload aria-hidden />
                                Upload signed file
                                <input
                                  type="file"
                                  accept=".json,.txt,text/json,application/json"
                                  onChange={handleVaultSignedFile}
                                />
                              </FilePickLabel>
                            </PayloadActions>
                            <textarea
                              value={signedVaultInput}
                              onChange={(e) => setSignedVaultInput(e.target.value)}
                              placeholder="Paste signedTransactionHex or full signer JSON here…"
                            />
                          </PayloadBox>
                        </>
                      )}

                      <ActionRow>
                        <SecondaryButton
                          type="button"
                          onClick={resetVaultOfflineFlow}
                          disabled={vaultBusy || (!preparedVault && !signedVaultInput)}
                        >
                          <FiRefreshCw />
                          Clear prepared / signed
                        </SecondaryButton>
                        <PrimaryButton
                          type="button"
                          onClick={handlePrepareVault}
                          disabled={
                            vaultBusy || Boolean(preparedVault) || !hasConfiguredCustodyVault || !signerAddress.trim()
                          }
                        >
                          <FiCpu />
                          Prepare transaction 1 (vault lock)
                        </PrimaryButton>
                        <PrimaryButton
                          type="button"
                          onClick={handleSubmitVault}
                          disabled={
                            vaultBusy || !preparedVault || !signedVaultInput.trim() || !hasConfiguredCustodyVault
                          }
                        >
                          <FiCpu />
                          Broadcast transaction 1 (vault lock)
                        </PrimaryButton>
                      </ActionRow>
                    </>
                  )}

                  {vaultLocked && (
                    <p style={{ color: '#86efac', fontSize: '0.88rem', marginBottom: '1rem' }}>
                      NFT is locked in the vault.
                    </p>
                  )}
                  {vaultPending && (
                    <p style={{ color: '#fcd34d', fontSize: '0.88rem', marginBottom: '1rem' }}>
                      Vault transaction submitted; poll the hash above or refresh until status is Locked.
                    </p>
                  )}

                  <SectionDivider />

                  <ManualSectionTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <StepNum $done={fractionDone} style={{ width: '1.65rem', height: '1.65rem', fontSize: '0.75rem' }}>
                      2
                    </StepNum>
                    <FiLayers style={{ color: '#93c5fd' }} aria-hidden />
                    Mint ERC-20 share tokens (transaction 2)
                  </ManualSectionTitle>

                  {!vaultLocked && (
                    <ManualLead style={{ marginBottom: '1rem' }}>
                      Complete <strong>transaction 1</strong> (vault lock) first. After the NFT is locked, you can prepare
                      and broadcast the <strong>transaction 2</strong> mint that issues ERC-20 shares for that locked NFT.
                    </ManualLead>
                  )}

                  {vaultLocked && !fractionDone && (
                    <>
                      <ManualLead>
                        This second transaction mints ERC-20 tokens on your fraction contract. Enter how many{' '}
                        <strong>share tokens</strong> to issue (whole or decimal); the app converts that to the raw integer
                        amount (wei) for <code>{fractionFunctionName || 'mint'}</code> using the decimals you select — they
                        must match the fraction token&apos;s <code>decimals()</code> on-chain. Those tokens represent
                        fractional ownership of the NFT now held in the vault.
                      </ManualLead>

                      <FormPanel>
                        <Row>
                          <Field>
                            <span className="label">Number of ERC-20 share tokens to mint</span>
                            <input
                              value={shareQuantityInput}
                              onChange={(e) => setShareQuantityInput(e.target.value)}
                              placeholder="e.g. 1000000"
                              autoComplete="off"
                            />
                            <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                              Whole or decimal number (e.g. <code>1.5</code> with 18 decimals). This is the share supply
                              minted in one transaction for the locked NFT.
                            </span>
                          </Field>
                          <Field>
                            <span className="label">Share token decimals</span>
                            <select
                              value={String(shareTokenDecimals)}
                              onChange={(e) => setShareTokenDecimals(Number(e.target.value))}
                              style={{
                                padding: '0.55rem 0.65rem',
                                borderRadius: 8,
                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                background: 'rgba(15, 15, 35, 0.8)',
                                color: '#fff',
                                fontSize: '0.92rem'
                              }}
                            >
                              <option value="6">6 (e.g. USDC-style)</option>
                              <option value="8">8</option>
                              <option value="18">18 (standard ERC-20)</option>
                            </select>
                            <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                              Must match your fraction token contract&apos;s <code>decimals()</code>.
                            </span>
                          </Field>
                        </Row>

                        {shareAmountValid ? (
                          <SharePreviewPanel>
                            <div>
                              Transaction 2 will mint{' '}
                              <span className="big">
                                {shareQuantityInput.trim() || '0'}
                              </span>{' '}
                              {tokenSymbol.trim() ? (
                                <>
                                  <strong>{tokenSymbol.trim()}</strong>
                                </>
                              ) : (
                                'ERC-20 share tokens'
                              )}{' '}
                              (on-chain raw amount: <code style={{ fontSize: '0.8em' }}>{fractionAmountWei}</code> wei,{' '}
                              {shareTokenDecimals} decimals).
                            </div>
                            <div className="sub">
                              Recipient:{' '}
                              {recipientWallet.trim()
                                ? shortenAddr(recipientWallet)
                                : tokenizerSimpleFractionUi
                                  ? '— (set offline signer above)'
                                  : '— (set below)'}{' '}
                              · Fraction contract:{' '}
                              {fractionContractAddress.trim() ? shortenAddr(fractionContractAddress) : '—'}
                            </div>
                          </SharePreviewPanel>
                        ) : (
                          <Warn style={{ marginBottom: '1rem' }}>
                            Enter a valid share quantity (digits and at most one decimal point, matching ERC-20 parse rules).
                          </Warn>
                        )}

                        {tokenizerSimpleFractionUi ? (
                          <div
                            style={{
                              marginBottom: '1rem',
                              padding: '0.9rem 1rem',
                              borderRadius: 10,
                              border: '1px solid rgba(148, 163, 184, 0.22)',
                              background: 'rgba(255, 255, 255, 0.03)',
                              fontSize: '0.86rem',
                              lineHeight: 1.55,
                              color: '#cbd5e1'
                            }}
                          >
                            <div style={{ fontWeight: 600, color: '#e2e8f0', marginBottom: '0.55rem' }}>
                              Share token (from your asset class)
                            </div>
                            <p style={{ margin: '0.35rem 0' }}>
                              <span style={{ color: '#94a3b8' }}>Contract</span>{' '}
                              <code
                                style={{ fontSize: '0.8rem', wordBreak: 'break-all' }}
                                title={fractionContractAddress || undefined}
                              >
                                {fractionContractAddress.trim()
                                  ? shortenAddr(fractionContractAddress)
                                  : '— not configured —'}
                              </code>
                            </p>
                            <p style={{ margin: '0.35rem 0 0.75rem' }}>
                              <span style={{ color: '#94a3b8' }}>Mint function</span>{' '}
                              <code>{fractionFunctionName || 'mint'}</code>
                            </p>
                            <Row style={{ marginBottom: 0 }}>
                              <Field style={{ marginBottom: '0.65rem' }}>
                                <span className="label">Token name (recorded)</span>
                                <input
                                  value={tokenName}
                                  onChange={(e) => setTokenName(e.target.value)}
                                  placeholder="Prefilled from asset class"
                                  autoComplete="off"
                                />
                              </Field>
                              <Field style={{ marginBottom: '0.65rem' }}>
                                <span className="label">Symbol (recorded)</span>
                                <input
                                  value={tokenSymbol}
                                  onChange={(e) => setTokenSymbol(e.target.value)}
                                  placeholder="Prefilled from asset class"
                                  autoComplete="off"
                                />
                              </Field>
                            </Row>
                            <Field style={{ marginBottom: '0.35rem' }}>
                              <span className="label">Recipient (receives new shares)</span>
                              <input
                                value={recipientWallet}
                                onChange={(e) => setRecipientWallet(e.target.value)}
                                placeholder="0x… (prefilled from offline signer)"
                                autoComplete="off"
                              />
                              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                                  Usually your offline signer wallet; you can paste a different recipient if needed.
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const s = String(signerAddress || '').trim();
                                    if (s) setRecipientWallet(s);
                                    else toast.error('Set offline signer address first.');
                                  }}
                                  style={{
                                    background: 'rgba(255,255,255,0.08)',
                                    border: '1px solid rgba(255,255,255,0.18)',
                                    borderRadius: 6,
                                    color: '#93c5fd',
                                    fontSize: '0.76rem',
                                    padding: '0.25rem 0.5rem',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Use offline signer
                                </button>
                              </div>
                            </Field>
                            <p style={{ margin: '0.65rem 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                              Contract ABI is applied automatically for the standard share token. Name, symbol, and
                              recipient are prefilled but editable. Admins can change contract and ABI in the full admin
                              flow.
                            </p>
                          </div>
                        ) : (
                          <>
                            <Row>
                              <Field>
                                <span className="label">Fraction token contract</span>
                                <input
                                  value={fractionContractAddress}
                                  onChange={(e) => setFractionContractAddress(e.target.value)}
                                  placeholder="0x…"
                                  autoComplete="off"
                                />
                              </Field>
                              <Field>
                                <span className="label">Mint function name</span>
                                <input
                                  value={fractionFunctionName}
                                  onChange={(e) => setFractionFunctionName(e.target.value)}
                                  placeholder="mint"
                                  autoComplete="off"
                                />
                              </Field>
                            </Row>
                            <Row>
                              <Field>
                                <span className="label">Recipient wallet (receives the new shares)</span>
                                <input
                                  value={recipientWallet}
                                  onChange={(e) => setRecipientWallet(e.target.value)}
                                  placeholder="0x…"
                                  autoComplete="off"
                                />
                              </Field>
                              <Field>
                                <span className="label">Token display name (recorded)</span>
                                <input
                                  value={tokenName}
                                  onChange={(e) => setTokenName(e.target.value)}
                                  placeholder="Name"
                                />
                              </Field>
                            </Row>
                            <Field>
                              <span className="label">Token symbol (recorded)</span>
                              <input
                                value={tokenSymbol}
                                onChange={(e) => setTokenSymbol(e.target.value)}
                                placeholder="SYM"
                              />
                            </Field>
                            <Field>
                              <span className="label">Fraction token contract ABI (JSON)</span>
                              <textarea
                                value={fractionAbi}
                                onChange={(e) => setFractionAbi(e.target.value)}
                                rows={5}
                                placeholder="Paste ERC-20 mint ABI…"
                              />
                            </Field>
                          </>
                        )}
                      </FormPanel>

                      <StatusPanel>
                        <div className="title">Transaction 2 — Offline signing &amp; broadcast (Ping chain)</div>
                        <FlowSteps>
                          <li>
                            <strong>Prepare unsigned ERC-20 mint</strong> — The API encodes{' '}
                            <code>{fractionFunctionName || 'mint'}</code> for the amount above (raw wei sent to the chain).
                          </li>
                          <li>
                            <strong>Sign</strong> — Use the same offline signer address if it is authorized to mint on the
                            fraction token contract.
                          </li>
                          <li>
                            <strong>Upload or paste signed data</strong>, then{' '}
                            <strong>Broadcast transaction 2 (fractional mint)</strong> — a separate{' '}
                            <code>eth_sendRawTransaction</code> from transaction 1.
                          </li>
                        </FlowSteps>
                        <div className="meta">
                          <span>Shares: {shareAmountValid ? shareQuantityInput.trim() : '—'}</span>
                          <span>Mint: {fractionFunctionName || 'mint'}</span>
                        </div>
                      </StatusPanel>

                      {preparedFraction && (
                        <>
                          <PayloadBox>
                            <div className="title">Transaction 2 — Unsigned ERC-20 share mint</div>
                            <div className="hint">Sign offline only — do not sign in the browser.</div>
                            <PayloadActions>
                              <MiniButton type="button" onClick={handleCopyFractionPayload}>
                                <FiCopy /> Copy JSON
                              </MiniButton>
                              <MiniButton
                                type="button"
                                onClick={() =>
                                  downloadJsonFile(`fractional-${asset.id}-prepared.json`, preparedFraction)
                                }
                              >
                                <FiDownload /> Download JSON
                              </MiniButton>
                            </PayloadActions>
                            <textarea readOnly value={formatPreparedPayload(preparedFraction)} />
                          </PayloadBox>

                          <PayloadBox>
                            <div className="title">Signer output template</div>
                            <div className="hint">Your signer should produce JSON containing signedTransactionHex.</div>
                            <PayloadActions>
                              <MiniButton
                                type="button"
                                onClick={async () => {
                                  await copyTextToClipboard(fractionSignerTemplate);
                                  toast.success('Copied template');
                                }}
                              >
                                <FiCopy /> Copy template
                              </MiniButton>
                            </PayloadActions>
                            <textarea readOnly value={fractionSignerTemplate} />
                          </PayloadBox>

                          <PayloadBox>
                            <div className="title">Transaction 2 — Signed ERC-20 mint — upload or paste</div>
                            <div className="hint">
                              Upload a JSON file from your signer or paste JSON / raw{' '}
                              <code>signedTransactionHex</code>.
                            </div>
                            <PayloadActions>
                              <FilePickLabel>
                                <FiUpload aria-hidden />
                                Upload signed file
                                <input
                                  type="file"
                                  accept=".json,.txt,text/json,application/json"
                                  onChange={handleFractionSignedFile}
                                />
                              </FilePickLabel>
                            </PayloadActions>
                            <textarea
                              value={signedFractionInput}
                              onChange={(e) => setSignedFractionInput(e.target.value)}
                              placeholder="Paste signedTransactionHex or full signer JSON here…"
                            />
                          </PayloadBox>
                        </>
                      )}

                      <ActionRow>
                        <SecondaryButton
                          type="button"
                          onClick={resetFractionOfflineFlow}
                          disabled={fractionBusy || (!preparedFraction && !signedFractionInput)}
                        >
                          <FiRefreshCw />
                          Clear prepared / signed
                        </SecondaryButton>
                        <PrimaryButton
                          type="button"
                          onClick={handlePrepareFraction}
                          disabled={
                            fractionBusy ||
                            Boolean(preparedFraction) ||
                            !signerAddress.trim() ||
                            !shareAmountValid ||
                            !fractionContractAddress.trim() ||
                            !normalizeEvmContractAbiJson(fractionAbi).trim() ||
                            !recipientWallet.trim()
                          }
                        >
                          <FiCpu />
                          Prepare transaction 2 (ERC-20 mint)
                        </PrimaryButton>
                        <PrimaryButton
                          type="button"
                          onClick={handleSubmitFraction}
                          disabled={fractionBusy || !preparedFraction || !signedFractionInput.trim() || !shareAmountValid}
                        >
                          <FiCpu />
                          Broadcast transaction 2 (ERC-20 mint)
                        </PrimaryButton>
                      </ActionRow>
                    </>
                  )}

                  {fractional && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <StatusPill>Fractional: {formatEnumLabel(fractional.status)}</StatusPill>
                      {fractional.mintTransactionHash && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <HashLink type="button" onClick={() => setTxDrawerHash(fractional.mintTransactionHash)}>
                            {fractional.mintTransactionHash}
                          </HashLink>
                        </div>
                      )}
                    </div>
                  )}

                  {fractionDone && fractional && (
                    <SharePreviewPanel style={{ marginTop: '0.75rem' }}>
                      <div>
                        <strong>Transaction 2 complete.</strong> ERC-20 share mint recorded for the locked NFT
                        {(() => {
                          const sym = String(
                            fractional.tokenSymbol ?? fractional.TokenSymbol ?? tokenSymbol ?? ''
                          ).trim();
                          return sym ? (
                            <>
                              {' '}
                              (<strong>{sym}</strong>)
                            </>
                          ) : null;
                        })()}
                        .
                      </div>
                      <div className="sub" style={{ marginTop: '0.5rem' }}>
                        {(() => {
                          const w = String(fractional.amount ?? fractional.Amount ?? '').trim();
                          let human =
                            formatWeiAsShares(w, shareTokenDecimals) ?? formatWeiAsShares(w, 18);
                          if (human != null) {
                            return (
                              <>
                                Minted: <span className="big">{human}</span> share tokens
                                {w ? (
                                  <>
                                    {' '}
                                    <span style={{ opacity: 0.85 }}>
                                      (on-chain raw <code>{w}</code> wei)
                                    </span>
                                  </>
                                ) : null}
                              </>
                            );
                          }
                          return w ? <>Minted raw amount: {w} wei</> : 'See transaction details for minted amount.';
                        })()}
                      </div>
                    </SharePreviewPanel>
                  )}
                  {fractionPending && (
                    <p style={{ color: '#fcd34d', fontSize: '0.88rem', marginTop: '0.5rem' }}>
                      Fractional mint submitted; confirm on-chain via the transaction hash.
                    </p>
                  )}
                </>
              )}
            </Body>
          </Surface>
        </Overlay>
      </AnimatePresence>
    </>
  );
}

export default VaultFractionalizationModal;
